import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-[var(--button-default-bg)] text-[var(--button-default-text)] border-[var(--button-default-border)] hover:bg-[var(--button-default-hover-bg)] active:bg-[var(--button-default-active-bg)]",
        destructive:
          "bg-[var(--button-destructive-bg)] text-[var(--button-destructive-text)] border-[var(--button-destructive-border)] hover:bg-[var(--button-destructive-hover-bg)] active:bg-[var(--button-destructive-active-bg)]",
        outline:
          "border border-[var(--button-outline-border)] bg-[var(--button-outline-bg)] text-[var(--button-outline-text)] hover:bg-[var(--button-outline-hover-bg)] hover:text-[var(--button-outline-hover-text)] active:bg-[var(--button-outline-active-bg)]",
        secondary:
          "bg-[var(--button-secondary-bg)] text-[var(--button-secondary-text)] border-[var(--button-secondary-border)] hover:bg-[var(--button-secondary-hover-bg)] active:bg-[var(--button-secondary-active-bg)]",
        ghost:
          "bg-[var(--button-ghost-bg)] text-[var(--button-ghost-text)] border-[var(--button-ghost-border)] hover:bg-[var(--button-ghost-hover-bg)] hover:text-[var(--button-ghost-hover-text)] active:bg-[var(--button-ghost-active-bg)]",
        link:
          "bg-[var(--button-link-bg)] text-[var(--button-link-text)] border-[var(--button-link-border)] underline-offset-4 hover:underline hover:text-[var(--button-link-hover-text)] active:text-[var(--button-link-active-text)]",
        success: // Added success variant
          "bg-[var(--button-success-bg)] text-[var(--button-success-text)] border-[var(--button-success-border)] hover:bg-[var(--button-success-hover-bg)] active:bg-[var(--button-success-active-bg)]",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
